/*
 * Toolbar: a row of buttons, menus, select buttons, inputs, titles and custom
 * HTML, driven by a small data API so a toolbar can be described once and then
 * changed item by item.
 *
 *   const tb = new dfc.Toolbar('#bar', { items: [
 *     { id: 'save', value: 'Save', icon: 'fa-light fa-floppy-disk' },
 *     { type: 'separator' },
 *     { id: 'view', type: 'selectButton', value: 'Grid', items: [{ id: 'grid', value: 'Grid' }, { id: 'board', value: 'Board' }] },
 *     { type: 'spacer' },
 *     { id: 'search', type: 'input', placeholder: 'Search', icon: 'search' },
 *   ] });
 *   tb.events.on('click', (id, e) => { ... });     // positional handlers
 *   tb.on('click', ({ id, item }) => { ... });     // payload handlers
 *   tb.data.update('save', { count: 3 });
 *
 * Item types: button (default; 'navItem', 'imageButton' and 'menuItem' are
 * read as buttons), selectButton, title, separator, spacer, input, customHTML.
 * Item properties: id, type, value (label), html, icon, src (image button),
 * tooltip, css (classes on the item), hidden, disabled, count, countColor,
 * twoState, active, group, items (a dropdown), placeholder, width, overflow
 * ('never' keeps it out of the overflow menu). Any other property is carried
 * through untouched and comes back from getItem and serialize.
 *
 * When the row is too narrow the trailing items move into a "more" menu.
 */

import { Component, h, uid, register } from './core.js';
import { renderIcon } from './icons.js';
import { ContextMenu } from './menu.js';
import { EventChannel } from './events.js';
import { showTip, hideTip } from './tooltip.js';

const BUTTONISH = new Set(['button', 'navItem', 'imageButton', 'menuItem', 'customHTMLButton']);
const PAYLOAD_NAMES = { click: 'click', input: 'input', inputchange: 'inputChange', inputfocus: 'inputFocus', inputblur: 'inputBlur', change: 'change', keydown: 'keydown', openmenu: 'openMenu' };

/** The rendering kind of an item: button, select, title, separator, spacer, input or html. */
function kindOf(item) {
	const t = item && item.type ? item.type : 'button';
	if (t === 'selectButton') return 'select';
	if (BUTTONISH.has(t)) return 'button';
	if (t === 'customHTML') return 'html';
	if (t === 'title' || t === 'separator' || t === 'spacer' || t === 'input') return t;
	return 'button';
}

function px(v) { return typeof v === 'number' ? v + 'px' : String(v); }
function key(id) { return String(id); }

export class Toolbar extends Component {
	static get componentName() { return 'toolbar'; }
	static get detachable() { return true; }

	defaults() {
		return {
			/** The items. `data` is accepted as another name for it. */
			items: null,
			/** Height of the bar (number of pixels or any CSS length). */
			height: null,
			/** Extra classes for the bar (in addition to className). */
			css: '',
			/** 'menu' moves items that do not fit into a "more" menu; 'scroll' scrolls; 'none' clips. */
			overflow: 'menu',
			/** Icon and accessible name of the overflow button. */
			moreIcon: 'ellipsis-vertical',
			moreLabel: 'More',
			/** Show item tooltips on hover and keyboard focus. */
			tooltips: true,
			/** Hover delay before a tooltip shows, in ms. */
			tooltipDelay: 350,
			/** Accessible name of the toolbar. */
			ariaLabel: '',
		};
	}

	// ── Setup and rendering ────────────────────────────────────────────────

	_init() {
		this._ready = true;
		this._root = [];
		this._byId = new Map();
		this._parent = new Map();
		this._nodes = new Map();
		this._overflowed = new Set();
		this._appliedCss = [];
		this.events = new EventChannel(this, (name, args) => this._behave(name, args));
		this.data = this._dataApi();
		const initial = this.options.items || this.options.data || [];
		this._parsedFrom = initial;
		this._addAll(initial, null, null);
	}

	render() {
		if (!this._ready) this._init();
		// update({ items }) replaces the items, like data.parse.
		const given = this.options.items || this.options.data || null;
		if (given && given !== this._parsedFrom) {
			this._parsedFrom = given;
			this._closeMenu();
			this._root = []; this._byId.clear(); this._parent.clear();
			this._addAll(given, null, null);
		}
		const el = this.el;
		this._appliedCss.forEach((c) => el.classList.remove(c));
		this._appliedCss = String(this.options.css || '').split(/\s+/).filter(Boolean);
		this._appliedCss.forEach((c) => el.classList.add(c));
		el.setAttribute('role', 'toolbar');
		el.setAttribute('aria-orientation', 'horizontal');
		if (this.options.ariaLabel) el.setAttribute('aria-label', this.options.ariaLabel); else el.removeAttribute('aria-label');
		el.style.height = this.options.height === null || this.options.height === undefined ? '' : px(this.options.height);
		el.classList.toggle('dfc-toolbar--scroll', this.options.overflow === 'scroll');
		if (!this._list) {
			this._list = h('div', { className: 'dfc-toolbar__items' });
			el.appendChild(this._list);
		}
		this._renderAll();
	}

	/** Rebuilds every top-level item. */
	_renderAll() {
		hideTip();
		const focusedId = this._focusedId();
		this._list.innerHTML = '';
		this._nodes.clear();
		for (const item of this._root) {
			const node = this._build(item);
			this._nodes.set(key(item.id), node);
			this._list.appendChild(node);
		}
		this._moreWrap = h('div', { className: 'dfc-toolbar__item dfc-toolbar__item--more', hidden: true });
		this._moreBtn = h('button', { type: 'button', className: 'dfc-toolbar__button dfc-toolbar__more is-icon-only', tabIndex: -1, 'aria-haspopup': 'menu', 'aria-expanded': 'false', 'aria-label': this.options.moreLabel, title: null },
			renderIcon(this.options.moreIcon));
		this._moreWrap.appendChild(this._moreBtn);
		this._list.appendChild(this._moreWrap);
		this._overflowed.clear();
		this._syncTabStop();
		if (focusedId !== null) this._refocus(focusedId);
		this._scheduleFit();
	}

	/** Rebuilds one top-level item in place, keeping focus if it was inside it. */
	_rerender(item) {
		const top = this._topOf(item);
		if (!top) return;
		const old = this._nodes.get(key(top.id));
		if (!old) { this._renderAll(); return; }
		const hadFocus = old.contains(document.activeElement);
		let caret = null;
		if (hadFocus && document.activeElement.tagName === 'INPUT') caret = document.activeElement.selectionStart;
		const node = this._build(top);
		if (this._overflowed.has(key(top.id))) node.classList.add('dfc-toolbar__item--overflow');
		old.replaceWith(node);
		this._nodes.set(key(top.id), node);
		if (this._menuFor === key(top.id)) { const b = this._control(top.id); if (b) b.setAttribute('aria-expanded', 'true'); }
		this._syncTabStop();
		if (hadFocus) this._refocus(top.id, caret);
		this._scheduleFit();
	}

	/** Builds the element for one top-level item. */
	_build(item) {
		const kind = kindOf(item);
		const wrap = h('div', { className: 'dfc-toolbar__item dfc-toolbar__item--' + kind });
		if (item.css) String(item.css).split(/\s+/).filter(Boolean).forEach((c) => wrap.classList.add(c));
		if (item.active) wrap.classList.add('is-active');
		if (item.disabled) wrap.classList.add('is-disabled');
		wrap.dataset.id = key(item.id);
		if (item.hidden) wrap.hidden = true;

		if (kind === 'separator') {
			wrap.setAttribute('role', 'separator');
			wrap.setAttribute('aria-orientation', 'vertical');
		} else if (kind === 'title') {
			const t = h('span', { className: 'dfc-toolbar__title' });
			if (item.html) t.innerHTML = item.html; else t.textContent = item.value === undefined || item.value === null ? '' : String(item.value);
			wrap.appendChild(t);
		} else if (kind === 'html') {
			const box = h('div', { className: 'dfc-toolbar__html', html: item.html || '' });
			if (item.width) wrap.style.width = px(item.width);
			wrap.appendChild(box);
		} else if (kind === 'input') {
			const field = h('label', { className: 'dfc-toolbar__field' + (item.icon ? ' has-icon' : '') });
			field.style.width = item.width ? px(item.width) : '200px';
			const icon = renderIcon(item.icon, 'dfc-toolbar__field-icon');
			if (icon) field.appendChild(icon);
			const input = h('input', {
				type: 'text', className: 'dfc-toolbar__input', placeholder: item.placeholder || null,
				'aria-label': item.label || item.tooltip || item.placeholder || key(item.id),
				autocomplete: 'off', disabled: !!item.disabled,
			});
			input.value = item.value === undefined || item.value === null ? '' : String(item.value);
			input.dataset.id = key(item.id);
			field.appendChild(input);
			wrap.appendChild(field);
		} else if (kind === 'button' || kind === 'select') {
			wrap.appendChild(this._buildButton(item, kind));
		}
		return wrap;
	}

	_buildButton(item, kind) {
		const label = item.value === undefined || item.value === null || item.value === '' ? (item.text === undefined || item.text === null ? '' : String(item.text)) : String(item.value);
		const menu = kind === 'select' || this._hasMenu(item);
		const toggles = !menu && (item.twoState || item.group);
		const iconOnly = !label && !item.html;
		const btn = h('button', {
			type: 'button',
			className: 'dfc-toolbar__button' + (iconOnly ? ' is-icon-only' : '') + (item.active ? ' is-active' : '') + (kind === 'select' ? ' dfc-toolbar__select' : ''),
			tabIndex: -1,
			'aria-disabled': item.disabled ? 'true' : null,
			'aria-pressed': toggles ? (item.active ? 'true' : 'false') : null,
			'aria-haspopup': menu ? 'menu' : null,
			'aria-expanded': menu ? 'false' : null,
			'aria-label': iconOnly ? (item.tooltip || key(item.id)) : (kind === 'select' && item.tooltip ? item.tooltip + ': ' + label : null),
		});
		btn.dataset.id = key(item.id);
		if (item.src) {
			const img = h('img', { className: 'dfc-toolbar__image', src: item.src, alt: '' });
			img.addEventListener('error', () => { const fallback = renderIcon(item.icon || 'user', 'dfc-toolbar__icon'); if (fallback) img.replaceWith(fallback); else img.remove(); }, { once: true });
			btn.appendChild(img);
		} else {
			const icon = renderIcon(item.icon, 'dfc-toolbar__icon');
			if (icon) btn.appendChild(icon);
		}
		if (item.html) btn.appendChild(h('span', { className: 'dfc-toolbar__text', html: item.html }));
		else if (label) btn.appendChild(h('span', { className: 'dfc-toolbar__text', text: label }));
		const count = Number(item.count);
		if (count > 0) {
			btn.appendChild(h('span', { className: 'dfc-toolbar__count dfc-toolbar__count--' + (item.countColor || 'danger'), text: count > 99 ? '99+' : String(count) }));
		}
		if (menu) btn.appendChild(renderIcon('chevron-down', 'dfc-toolbar__caret'));
		return btn;
	}

	// ── Data ───────────────────────────────────────────────────────────────

	_hasMenu(item) { return !!(item && Array.isArray(item.items) && item.items.length); }
	_children(item) { return item ? (item.items || []) : this._root; }

	/** Copies one item (and its sub-items) into the store under a parent. */
	_store(raw, parent, index) {
		const item = Object.assign({}, raw);
		if (item.id === undefined || item.id === null || item.id === '') item.id = uid('dfc-tbi');
		if (this._byId.has(key(item.id))) {
			console.warn('dfc-toolbar: duplicate item id "' + item.id + '"; the new item replaces the old one');
			this._removeOne(item.id);
		}
		delete item.parent;
		const kids = Array.isArray(raw.items) ? raw.items : null;
		if (kids) item.items = [];
		this._byId.set(key(item.id), item);
		this._parent.set(key(item.id), parent);
		const list = this._children(parent);
		if (index === undefined || index === null || index < 0 || index > list.length) list.push(item);
		else list.splice(index, 0, item);
		if (kids) kids.forEach((k) => this._store(k, item, null));
		return item;
	}

	_addAll(items, parent, index) {
		const ids = [];
		(Array.isArray(items) ? items : [items]).forEach((raw, i) => {
			if (!raw || typeof raw !== 'object') return;
			const at = index === null || index === undefined ? null : index + i;
			ids.push(this._store(raw, parent, at).id);
		});
		return ids;
	}

	_removeOne(id) {
		const item = this._byId.get(key(id));
		if (!item) return null;
		const parent = this._parent.get(key(id)) || null;
		const list = this._children(parent);
		const at = list.indexOf(item);
		if (at >= 0) list.splice(at, 1);
		const drop = (it) => { (it.items || []).forEach(drop); this._byId.delete(key(it.id)); this._parent.delete(key(it.id)); };
		drop(item);
		return parent;
	}

	_topOf(item) {
		let it = item;
		while (it && this._parent.get(key(it.id))) it = this._parent.get(key(it.id));
		return it || null;
	}

	/** A deep copy of an item, without anything the toolbar keeps for itself. */
	_copy(item) {
		const out = Object.assign({}, item);
		if (Array.isArray(item.items)) out.items = item.items.map((k) => this._copy(k));
		return out;
	}

	/** Every item in the tree, depth first. */
	_all() {
		const out = [];
		const walk = (list) => list.forEach((it) => { out.push(it); if (it.items) walk(it.items); });
		walk(this._root);
		return out;
	}

	/** Builds the `data` object: the item store's public API. */
	_dataApi() {
		const self = this;
		const changed = (item) => { if (self._destroyed) return; if (item) self._rerender(item); else self._renderAll(); };
		return {
			/** Replaces every item. */
			parse(items) {
				self._closeMenu();
				self._root = []; self._byId.clear(); self._parent.clear();
				self._addAll(items || [], null, null);
				changed(null);
				return self.data;
			},
			/**
			 * Adds an item (or an array of items) at an index, to the root or, when
			 * the item names a `parent`, to that item's dropdown. Returns the id (or ids).
			 */
			add(item, index) {
				const first = Array.isArray(item) ? item[0] : item;
				const parentId = first && first.parent !== undefined && first.parent !== null ? first.parent : null;
				const parent = parentId === null ? null : self._byId.get(key(parentId));
				if (parentId !== null && !parent) return Array.isArray(item) ? [] : undefined;
				if (parent && !Array.isArray(parent.items)) parent.items = [];
				const ids = self._addAll(item, parent, index);
				changed(parent);
				return Array.isArray(item) ? ids : ids[0];
			},
			/** Merges a patch into an item. `items` in the patch replaces its dropdown. */
			update(id, patch) {
				const item = self._byId.get(key(id));
				if (!item || !patch) return;
				const rest = Object.assign({}, patch);
				delete rest.id; delete rest.parent;
				if ('items' in rest) {
					(item.items || []).slice().forEach((k) => self._removeOne(k.id));
					const kids = rest.items;
					delete rest.items;
					item.items = Array.isArray(kids) ? [] : undefined;
					if (Array.isArray(kids)) self._addAll(kids, item, null);
				}
				Object.assign(item, rest);
				if (self._menuFor && self._topOf(item) && key(self._topOf(item).id) === self._menuFor && self._parent.get(key(id))) self._closeMenu();
				// An input's value, placeholder, disabled and hidden are patched in place, so
				// a host writing state while the user types keeps focus and caret.
				if (kindOf(item) === 'input' && !self._parent.get(key(id)) && Object.keys(rest).every((k) => ['value', 'placeholder', 'disabled', 'hidden'].includes(k))) {
					const node = self._nodes.get(key(id));
					const input = node && node.querySelector('input');
					if (input) {
						const v = item.value === undefined || item.value === null ? '' : String(item.value);
						if (input.value !== v) input.value = v;
						if ('placeholder' in rest) input.placeholder = item.placeholder || '';
						if ('disabled' in rest) input.disabled = !!item.disabled;
						if ('hidden' in rest) { node.hidden = !!item.hidden; self._scheduleFit(); }
						return;
					}
				}
				changed(item);
			},
			/** Removes items (and their sub-items) by id. */
			remove(ids) {
				const list = Array.isArray(ids) ? ids : [ids];
				let any = false; const parents = new Set();
				list.forEach((id) => { if (self._byId.has(key(id))) { any = true; parents.add(self._removeOne(id)); } });
				if (!any) return;
				if (parents.has(null) || parents.size !== 1) changed(null); else changed([...parents][0]);
			},
			removeAll() { self._closeMenu(); self._root = []; self._byId.clear(); self._parent.clear(); changed(null); },
			/** The live item, or undefined. */
			getItem(id) { return self._byId.get(key(id)); },
			exists(id) { return self._byId.has(key(id)); },
			/** Position of an item among its siblings, or -1. */
			getIndex(id) { const it = self._byId.get(key(id)); if (!it) return -1; return self._children(self._parent.get(key(id)) || null).indexOf(it); },
			/** Id of the item at an index among the root items (or a parent's sub-items). */
			getId(index, parentId) { const p = parentId === undefined || parentId === null ? null : self._byId.get(key(parentId)); const it = self._children(p)[index]; return it ? it.id : undefined; },
			/** The live sub-items of a parent, or the root items. */
			getItems(parentId) { const p = parentId === undefined || parentId === null ? null : self._byId.get(key(parentId)); return parentId !== undefined && parentId !== null && !p ? [] : self._children(p).slice(); },
			getParent(id) { const p = self._parent.get(key(id)); return p ? p.id : undefined; },
			getLength(parentId) { return this.getItems(parentId).length; },
			forEach(fn, parentId) { this.getItems(parentId).forEach((it, i) => fn(it, i)); },
			map(fn, parentId) { return this.getItems(parentId).map((it, i) => fn(it, i)); },
			find(fn) { return self._all().find(fn); },
			/** A deep copy of the items, with sub-items under `items`. */
			serialize() { return self._root.map((it) => self._copy(it)); },
		};
	}

	// ── State ──────────────────────────────────────────────────────────────

	/** Applies a property to ids (one, an array, or every root item when omitted). */
	_setProp(ids, prop, value) {
		const list = ids === undefined || ids === null ? this._root.map((it) => it.id) : (Array.isArray(ids) ? ids : [ids]);
		list.forEach((id) => { const it = this._byId.get(key(id)); if (it && it[prop] !== value) this.data.update(id, { [prop]: value }); });
		return this;
	}

	show(ids) { return this._setProp(ids, 'hidden', false); }
	hide(ids) { return this._setProp(ids, 'hidden', true); }
	enable(ids) { return this._setProp(ids, 'disabled', false); }
	disable(ids) { return this._setProp(ids, 'disabled', true); }
	isDisabled(id) { const it = this._byId.get(key(id)); return it ? !!it.disabled : undefined; }

	/** Marks an item active. In a group, the other members go inactive. */
	select(id) {
		const it = this._byId.get(key(id));
		if (!it) return this;
		if (it.group) this._all().filter((o) => o.group === it.group && o !== it && o.active).forEach((o) => this.data.update(o.id, { active: false }));
		if (!it.active) this.data.update(id, { active: true });
		return this;
	}

	/** Marks an item inactive, or every item when no id is given. */
	unselect(id) {
		if (id === undefined || id === null) { this._all().filter((o) => o.active).forEach((o) => this.data.update(o.id, { active: false })); return this; }
		const it = this._byId.get(key(id));
		if (it && it.active) this.data.update(id, { active: false });
		return this;
	}

	isSelected(id) { const it = this._byId.get(key(id)); return it ? !!it.active : undefined; }

	/**
	 * The state of the stateful items: an input's or select button's value,
	 * a two-state button's active flag, and each group's active item id (keyed
	 * by group name). With an id, that one entry.
	 */
	getState(id) {
		if (id !== undefined && id !== null && !this._byId.has(key(id))) return undefined;
		const state = {};
		this._all().forEach((it) => {
			const kind = kindOf(it);
			if (it.group) { if (it.active) state[it.group] = it.id; return; }
			if (it.twoState) state[it.id] = !!it.active;
			else if (kind === 'input' || kind === 'select') state[it.id] = it.value;
		});
		return id !== undefined && id !== null ? state[key(id)] : state;
	}

	/** Sets state in the shape getState returns. */
	setState(state) {
		Object.keys(state || {}).forEach((k) => {
			const v = state[k];
			const members = this._all().filter((o) => o.group === k);
			if (members.length) { if (v !== undefined && v !== null) this.select(v); return; }
			const it = this._byId.get(key(k));
			if (!it) return;
			const kind = kindOf(it);
			if (kind === 'input') this.data.update(k, { value: v });
			else if (kind === 'select') {
				const opt = (it.items || []).find((o) => o.value === v);
				this.data.update(k, opt && opt.icon ? { value: v, icon: opt.icon } : { value: v });
			} else this.data.update(k, { active: !!v });
		});
		return this;
	}

	/** Focuses an item's control. */
	setFocus(id) { const c = this._control(id); if (c) { if (c.tagName !== 'INPUT') this._setTabStop(c); c.focus(); } return this; }

	/** The element of a top-level item (its wrapper), or null. */
	itemElement(id) { return this._nodes.get(key(id)) || null; }

	/** Same as destroy(). */
	destructor() { this.destroy(); }

	// ── Behaviour ──────────────────────────────────────────────────────────

	/**
	 * Runs before the handlers of every fired event, including a host's own
	 * events.fire: a select button takes the value of the option clicked, a
	 * group moves its active item, and the payload listeners are told.
	 */
	_behave(name, args) {
		if (name === 'click') {
			const item = this._byId.get(key(args[0]));
			if (item) {
				const parent = this._parent.get(key(item.id));
				if (parent && kindOf(parent) === 'select' && item.type !== 'separator') {
					const previous = parent.value;
					const patch = { value: item.value };
					if (item.icon) patch.icon = item.icon;
					this.data.update(parent.id, patch);
					if (previous !== item.value) this.events.fire('change', [parent.id, item.value, item.id]);
				}
				if (item.group && !item.active) {
					this.select(item.id);
					this.events.fire('change', [item.id, true]);
				}
			}
			this.emit('click', { id: args[0], item: item || null, originalEvent: args[1] || null });
			return;
		}
		if (name === 'inputchange') {
			const item = this._byId.get(key(args[0]));
			if (item) item.value = args[1];
		}
		const payloadName = PAYLOAD_NAMES[name] || name;
		let payload;
		if (name === 'keydown') payload = { originalEvent: args[0], id: args[1] };
		else if (name === 'change') payload = { id: args[0], value: args[1], option: args[2] };
		else payload = { id: args[0], value: args[1] };
		this.emit(payloadName, payload);
	}

	/** A press on a leaf button: toggles a two-state button, then reports the click. */
	_press(id, ev) {
		const item = this._byId.get(key(id));
		if (!item || item.disabled) return;
		if (item.twoState && !item.group) {
			this.data.update(id, { active: !item.active });
			this.events.fire('change', [item.id, !!item.active]);
		}
		this.events.fire('click', [item.id, ev]);
	}

	_control(id) {
		const node = this._nodes.get(key(id));
		return node ? node.querySelector('button, input') : null;
	}

	bind() {
		this._onClick = (ev) => {
			hideTip();
			const wrap = ev.target.closest ? ev.target.closest('.dfc-toolbar__item') : null;
			if (!wrap || wrap.parentNode !== this._list) return;
			if (wrap === this._moreWrap) { this._toggleMore(); return; }
			const item = this._byId.get(wrap.dataset.id);
			if (!item || item.disabled) return;
			const kind = kindOf(item);
			if (kind === 'title' || kind === 'separator' || kind === 'spacer' || kind === 'input') return;
			if (kind === 'html') { this.events.fire('click', [item.id, ev]); return; }
			const btn = wrap.querySelector('.dfc-toolbar__button');
			if (btn) this._setTabStop(btn);
			if (kind === 'select' || this._hasMenu(item)) {
				if (this._menuFor === key(item.id)) { this._closeMenu(); return; }
				this._openMenu(item, btn);
				this.events.fire('click', [item.id, ev]);
				return;
			}
			this._press(item.id, ev);
		};
		this._onInput = (ev) => {
			const input = ev.target;
			if (!input.classList || !input.classList.contains('dfc-toolbar__input')) return;
			const item = this._byId.get(input.dataset.id);
			if (!item) return;
			item.value = input.value;
			this.events.fire('input', [item.id, input.value]);
		};
		this._onChange = (ev) => {
			const input = ev.target;
			if (!input.classList || !input.classList.contains('dfc-toolbar__input')) return;
			const item = this._byId.get(input.dataset.id);
			if (item) this.events.fire('inputChange', [item.id, input.value]);
		};
		this._onFocusIn = (ev) => {
			const t = ev.target;
			if (t.classList && t.classList.contains('dfc-toolbar__input')) { const it = this._byId.get(t.dataset.id); if (it) this.events.fire('inputFocus', [it.id]); return; }
			if (t.classList && t.classList.contains('dfc-toolbar__button')) {
				this._setTabStop(t);
				if (t.matches && t.matches(':focus-visible')) this._tipFor(t, 0);
			}
		};
		this._onFocusOut = (ev) => {
			const t = ev.target;
			hideTip();
			if (t.classList && t.classList.contains('dfc-toolbar__input')) { const it = this._byId.get(t.dataset.id); if (it) this.events.fire('inputBlur', [it.id]); }
		};
		this._onKey = (ev) => this._key(ev);
		this._onOver = (ev) => {
			const btn = ev.target.closest ? ev.target.closest('.dfc-toolbar__button, .dfc-toolbar__item--title, .dfc-toolbar__item--input') : null;
			if (!btn || btn === this._hoverEl) return;
			this._hoverEl = btn;
			this._tipFor(btn, this.options.tooltipDelay);
		};
		this._onOut = (ev) => {
			if (ev.relatedTarget && this._hoverEl && this._hoverEl.contains(ev.relatedTarget)) return;
			this._hoverEl = null;
			clearTimeout(this._tipTimer);
			hideTip();
		};
		const el = this.el;
		el.addEventListener('click', this._onClick);
		el.addEventListener('input', this._onInput);
		el.addEventListener('change', this._onChange);
		el.addEventListener('focusin', this._onFocusIn);
		el.addEventListener('focusout', this._onFocusOut);
		el.addEventListener('keydown', this._onKey);
		el.addEventListener('mouseover', this._onOver);
		el.addEventListener('mouseout', this._onOut);
		if (typeof ResizeObserver !== 'undefined') {
			this._ro = new ResizeObserver(() => this._scheduleFit());
			this._ro.observe(el);
		}
	}

	mounted() { this._scheduleFit(); }

	/** Shows the tooltip of the item under an element after a delay. */
	_tipFor(el, delay) {
		clearTimeout(this._tipTimer);
		if (!this.options.tooltips) return;
		const wrap = el.closest('.dfc-toolbar__item');
		let text;
		if (wrap === this._moreWrap) text = this.options.moreLabel;
		else { const item = wrap ? this._byId.get(wrap.dataset.id) : null; text = item && item.tooltip; }
		if (!text) return;
		this._tipTimer = setTimeout(() => { if (!this._menuFor && !this._destroyed) showTip(el, text, 'below'); }, delay);
	}

	_key(ev) {
		const t = ev.target;
		const wrap = t.closest ? t.closest('.dfc-toolbar__item') : null;
		const item = wrap && wrap !== this._moreWrap ? this._byId.get(wrap.dataset.id) : null;
		this.events.fire('keydown', [ev, item ? item.id : undefined]);
		if (t.tagName === 'INPUT' || ev.defaultPrevented) return;
		if (!t.classList || !t.classList.contains('dfc-toolbar__button')) return;
		const stops = this._stops();
		const at = stops.indexOf(t);
		const go = (i) => { const n = stops.length; if (!n) return; const b = stops[(i + n) % n]; this._setTabStop(b); b.focus(); };
		switch (ev.key) {
			case 'ArrowRight': ev.preventDefault(); go(at + 1); break;
			case 'ArrowLeft': ev.preventDefault(); go(at - 1); break;
			case 'Home': ev.preventDefault(); go(0); break;
			case 'End': ev.preventDefault(); go(stops.length - 1); break;
			case 'ArrowDown': case 'ArrowUp':
				if (t === this._moreBtn) { ev.preventDefault(); if (!this._menuFor) this._toggleMore(); }
				else if (item && (kindOf(item) === 'select' || this._hasMenu(item)) && !item.disabled) {
					ev.preventDefault();
					if (this._menuFor !== key(item.id)) { this._openMenu(item, t); this.events.fire('click', [item.id, ev]); }
				}
				break;
			default: break;
		}
	}

	// ── Roving focus ───────────────────────────────────────────────────────

	/** Buttons that take part in arrow-key movement, in order. */
	_stops() {
		const out = [];
		for (const node of this._list.children) {
			if (node.hidden || node.classList.contains('dfc-toolbar__item--overflow')) continue;
			const b = node.querySelector(':scope > .dfc-toolbar__button');
			if (b) out.push(b);
		}
		return out;
	}

	_setTabStop(btn) {
		this._list.querySelectorAll('.dfc-toolbar__button').forEach((b) => { b.tabIndex = b === btn ? 0 : -1; });
		this._stopId = btn === this._moreBtn ? '__more' : (btn.dataset.id || null);
	}

	_syncTabStop() {
		const stops = this._stops();
		let want = null;
		if (this._stopId === '__more' && stops.includes(this._moreBtn)) want = this._moreBtn;
		else if (this._stopId) want = stops.find((b) => b.dataset.id === this._stopId) || null;
		const first = want || stops.find((b) => b.getAttribute('aria-disabled') !== 'true') || stops[0];
		this._list.querySelectorAll('.dfc-toolbar__button').forEach((b) => { b.tabIndex = b === first ? 0 : -1; });
		this._list.querySelectorAll('.dfc-toolbar__input').forEach((i) => { i.tabIndex = 0; });
	}

	_focusedId() {
		const a = document.activeElement;
		if (!a || !this._list || !this._list.contains(a)) return null;
		const wrap = a.closest('.dfc-toolbar__item');
		return wrap && wrap.dataset.id ? wrap.dataset.id : null;
	}

	_refocus(id, caret) {
		const c = this._control(id);
		if (!c) return;
		if (c.tagName !== 'INPUT') this._setTabStop(c);
		c.focus({ preventScroll: true });
		if (c.tagName === 'INPUT' && typeof caret === 'number') { try { c.setSelectionRange(caret, caret); } catch (e) { /* ignore */ } }
	}

	// ── Menus ──────────────────────────────────────────────────────────────

	/** Menu entries for an item's sub-items. */
	_menuItems(parent) {
		const isSelect = kindOf(parent) === 'select';
		return (parent.items || []).filter((c) => !c.hidden).map((c) => {
			if (c.type === 'separator') return 'separator';
			if (c.type === 'title') return { name: c.value || '', disabled: true };
			const sub = this._hasMenu(c) ? this._menuItems(c) : null;
			const entry = {
				name: c.value === undefined || c.value === null ? (c.text || '') : String(c.value),
				icon: c.icon,
				disabled: !!c.disabled,
				cssClass: c.css || undefined,
				action: () => this._press(c.id),
			};
			if (isSelect) entry.checked = c.value === parent.value;
			else if (c.twoState || c.group) entry.checked = !!c.active;
			if (sub && sub.length) entry.subMenu = sub;
			return entry;
		});
	}

	_menu() {
		if (!this._ctxMenu) this._ctxMenu = new ContextMenu({ host: document.body, onClose: () => this._menuClosed() });
		return this._ctxMenu;
	}

	_openMenu(item, btn) {
		hideTip();
		clearTimeout(this._tipTimer);
		const anchor = btn || this._control(item.id);
		this._menu().show({ anchor, placement: 'below', items: this._menuItems(item), trigger: anchor, returnFocus: anchor });
		this._menuFor = key(item.id);
		if (anchor) anchor.setAttribute('aria-expanded', 'true');
		this.events.fire('openMenu', [item.id]);
	}

	_closeMenu() { if (this._ctxMenu && this._ctxMenu.open) this._ctxMenu.hide(); else this._menuClosed(); }

	_menuClosed() {
		if (this._menuFor === '__more') { if (this._moreBtn) this._moreBtn.setAttribute('aria-expanded', 'false'); }
		else if (this._menuFor) { const b = this._control(this._menuFor); if (b) b.setAttribute('aria-expanded', 'false'); }
		this._menuFor = null;
	}

	/** Opens or closes the overflow menu. */
	_toggleMore() {
		if (this._menuFor === '__more') { this._closeMenu(); return; }
		const entries = [];
		this._root.forEach((item) => {
			if (!this._overflowed.has(key(item.id)) || item.hidden) return;
			const kind = kindOf(item);
			if (kind === 'separator') { entries.push('separator'); return; }
			if (kind !== 'button' && kind !== 'select') return;
			const label = item.value === undefined || item.value === null || item.value === '' ? (item.tooltip || String(item.id)) : String(item.value);
			const entry = { name: kind === 'select' && item.tooltip ? item.tooltip + ': ' + label : label, icon: item.src ? null : item.icon, disabled: !!item.disabled };
			if (kind === 'select' || this._hasMenu(item)) entry.subMenu = this._menuItems(item);
			else {
				entry.action = () => this._press(item.id);
				if (item.twoState || item.group) entry.checked = !!item.active;
			}
			entries.push(entry);
		});
		hideTip();
		this._menu().show({ anchor: this._moreBtn, placement: 'below', items: entries, trigger: this._moreBtn, returnFocus: this._moreBtn });
		this._menuFor = '__more';
		this._moreBtn.setAttribute('aria-expanded', 'true');
	}

	// ── Overflow ───────────────────────────────────────────────────────────

	_scheduleFit() {
		if (this._fitFrame || this._destroyed) return;
		const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (fn) => setTimeout(fn, 16);
		this._fitFrame = raf(() => { this._fitFrame = null; this._fit(); });
	}

	/** Moves trailing items into the "more" menu until the row fits. */
	_fit() {
		if (this._destroyed || !this._list) return;
		const list = this._list;
		const was = new Set(this._overflowed);
		this._overflowed.clear();
		list.querySelectorAll('.dfc-toolbar__item--overflow').forEach((n) => n.classList.remove('dfc-toolbar__item--overflow'));
		this._moreWrap.hidden = true;
		if (this.options.overflow === 'menu' && this.el.isConnected && list.clientWidth > 0 && list.scrollWidth > list.clientWidth + 1) {
			this._moreWrap.hidden = false;
			for (let i = this._root.length - 1; i >= 0 && list.scrollWidth > list.clientWidth + 1; i--) {
				const item = this._root[i];
				const kind = kindOf(item);
				if (item.hidden || item.overflow === 'never' || kind === 'input' || kind === 'html' || kind === 'spacer') continue;
				const node = this._nodes.get(key(item.id));
				if (!node) continue;
				node.classList.add('dfc-toolbar__item--overflow');
				this._overflowed.add(key(item.id));
			}
			// Nothing worth showing in the menu: drop the button.
			const useful = this._root.some((it) => this._overflowed.has(key(it.id)) && !it.hidden && (kindOf(it) === 'button' || kindOf(it) === 'select'));
			if (!useful) this._moreWrap.hidden = true;
		}
		const same = was.size === this._overflowed.size && [...was].every((id) => this._overflowed.has(id));
		if (!same) {
			if (this._menuFor === '__more') this._closeMenu();
			this._syncTabStop();
		}
	}

	unbind() {
		hideTip();
		clearTimeout(this._tipTimer);
		if (this._fitFrame) { (typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame : clearTimeout)(this._fitFrame); this._fitFrame = null; }
		if (this._ro) { this._ro.disconnect(); this._ro = null; }
		if (this._ctxMenu) { this._ctxMenu.destroy(); this._ctxMenu = null; }
		const el = this.el;
		el.removeEventListener('click', this._onClick);
		el.removeEventListener('input', this._onInput);
		el.removeEventListener('change', this._onChange);
		el.removeEventListener('focusin', this._onFocusIn);
		el.removeEventListener('focusout', this._onFocusOut);
		el.removeEventListener('keydown', this._onKey);
		el.removeEventListener('mouseover', this._onOver);
		el.removeEventListener('mouseout', this._onOut);
		if (this.events) this.events.clear();
	}
}

register('toolbar', Toolbar);
export default Toolbar;
